import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { DASHBOARD } from "@/constants/testIds";
import { Plus, FileText, AlertTriangle, CheckCircle, Clock, Trash2, ChevronRight, AlertCircle, X, Search, SlidersHorizontal, ChevronDown, TrendingUp, User } from "lucide-react";

function DeleteConfirmModal({ report, onConfirm, onCancel, deleting }) {
  if (!report) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        data-testid={DASHBOARD.deleteModalBackdrop}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      {/* Modal */}
      <div
        data-testid={DASHBOARD.deleteModal}
        className="relative bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-150"
      >
        <button
          data-testid={DASHBOARD.deleteModalClose}
          onClick={onCancel}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors">
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Delete Report
            </h3>
            <p className="text-xs text-slate-400">This action cannot be undone</p>
          </div>
        </div>

        <p className="text-sm text-slate-600 mb-1">
          Are you sure you want to delete the report for:
        </p>
        <p className="text-sm font-semibold text-slate-900 mb-5 truncate">
          {report.patient_info?.name || "Unknown Patient"} — {report.original_filename}
        </p>

        <div className="flex gap-3">
          <button
            data-testid={DASHBOARD.deleteModalCancel}
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            data-testid={DASHBOARD.deleteModalConfirm}
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {deleting ? <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Deleting...</> : "Delete Report"}
          </button>
        </div>
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const map = {
    normal: "badge-normal",
    requires_attention: "badge-requires",
    critical: "badge-abnormal",
  };
  const labels = { normal: "Normal", requires_attention: "Needs Attention", critical: "Critical" };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status] || "badge-normal"}`}>
      {labels[status] || status}
    </span>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Search & filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  const openDeleteModal = (e, report) => {
    e.stopPropagation();
    setDeleteError("");
    setPendingDelete(report);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/reports/${pendingDelete._id}`, { withCredentials: true });
      setReports((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      setPendingDelete(null);
    } catch (e) {
      setDeleteError("Failed to delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const stats = {
    total: reports.length,
    normal: reports.filter((r) => r.overall_status === "normal").length,
    attention: reports.filter((r) => r.overall_status === "requires_attention").length,
    critical: reports.filter((r) => r.overall_status === "critical").length,
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  const getAbnormalCount = (r) =>
    (r.blood_markers || []).filter((m) => m.status === "abnormal" || m.status === "borderline").length;

  // ── Filtering & sorting ─────────────────────────────────────────────────
  const filteredReports = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateThresholds = {
      today:  startOfDay,
      week:   new Date(now - 7  * 86400000),
      month:  new Date(now - 30 * 86400000),
    };

    return reports
      .filter((r) => {
        // Name search
        const name = (r.patient_info?.name || "").toLowerCase();
        if (search.trim() && !name.includes(search.trim().toLowerCase())) return false;
        // Status filter
        if (statusFilter !== "all" && r.overall_status !== statusFilter) return false;
        // Date filter
        if (dateFilter !== "all") {
          const created = new Date(r.created_at);
          if (created < dateThresholds[dateFilter]) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === "oldest") return new Date(a.created_at) - new Date(b.created_at);
        if (sortBy === "name")   return (a.patient_info?.name || "").localeCompare(b.patient_info?.name || "");
        return new Date(b.created_at) - new Date(a.created_at); // newest
      });
  }, [reports, search, statusFilter, dateFilter, sortBy]);

  const hasActiveFilters = search.trim() || statusFilter !== "all" || dateFilter !== "all" || sortBy !== "newest";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFilter("all");
    setSortBy("newest");
  };

  // Patients with 2+ reports → show Trends strip
  const trendPatients = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      const name = r.patient_info?.name;
      if (name) { if (!map[name]) map[name] = 0; map[name]++; }
    });
    return Object.entries(map).filter(([, n]) => n >= 2);
  }, [reports]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid={DASHBOARD.page}>
      <Navbar />

      <DeleteConfirmModal
        report={pendingDelete}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        deleting={deleting}
      />

      {deleteError && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white text-sm px-4 py-3 rounded-lg shadow-lg z-40 flex items-center gap-2">
          <AlertCircle size={15} /> {deleteError}
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>My Reports</h1>
            <p className="text-slate-500 text-sm mt-1">Your blood test analyses history</p>
          </div>
          <button
            data-testid={DASHBOARD.newAnalysisButton}
            onClick={() => navigate("/upload")}
            className="flex items-center gap-2 bg-blue-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors">
            <Plus size={16} /> New Analysis
          </button>
        </div>

        {/* Stats */}
        {reports.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard icon={FileText}      value={stats.total}     label="Total Reports"   color="bg-blue-900" />
            <StatCard icon={CheckCircle}   value={stats.normal}    label="Normal"          color="bg-emerald-600" />
            <StatCard icon={AlertTriangle} value={stats.attention} label="Needs Attention" color="bg-amber-500" />
            <StatCard icon={AlertTriangle} value={stats.critical}  label="Critical"        color="bg-red-600" />
          </div>
        )}

        {/* Trends strip */}
        {trendPatients.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <TrendingUp size={14} className="text-blue-900" /> Patient Trends Available
              </h3>
              <Link to="/trends" className="text-xs text-blue-900 hover:underline font-medium">View all</Link>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {trendPatients.map(([name, count]) => (
                <Link key={name} to={`/trends?patient=${encodeURIComponent(name)}`}
                  data-testid={`${DASHBOARD.trendPatientLink}-${name}`}
                  className="flex-shrink-0 flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 hover:border-blue-300 hover:bg-blue-50 transition-colors group">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                    <User size={13} className="text-blue-900" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 leading-tight">{name}</p>
                    <p className="text-xs text-slate-400">{count} reports</p>
                  </div>
                  <TrendingUp size={12} className="text-blue-400 group-hover:text-blue-700 ml-1 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Search & filter bar */}
        {reports.length > 0 && (
          <div className="mb-6 space-y-3">
            {/* Row 1: search + filter toggle */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid={DASHBOARD.searchInput}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by patient name…"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                {search && (
                  <button onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                data-testid={DASHBOARD.filterToggleButton}
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-all ${
                  showFilters || (statusFilter !== "all" || dateFilter !== "all" || sortBy !== "newest")
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                }`}>
                <SlidersHorizontal size={14} /> Filters
                {(statusFilter !== "all" || dateFilter !== "all" || sortBy !== "newest") && (
                  <span className="w-2 h-2 bg-white rounded-full opacity-80" />
                )}
              </button>
            </div>

            {/* Row 2: filter pills (collapsible) */}
            {showFilters && (
              <div className="flex flex-wrap gap-3 p-4 bg-white border border-slate-200 rounded-lg">
                {/* Status */}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Status</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { val: "all",                label: "All" },
                      { val: "normal",             label: "Normal" },
                      { val: "requires_attention", label: "Needs Attention" },
                      { val: "critical",           label: "Critical" },
                    ].map(({ val, label }) => (
                      <button
                        key={val}
                        data-testid={`${DASHBOARD.statusFilterButton}-${val}`}
                        onClick={() => setStatusFilter(val)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                          statusFilter === val
                            ? "bg-blue-900 text-white border-blue-900"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px bg-slate-200 self-stretch mx-1 hidden sm:block" />

                {/* Date */}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Date Range</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { val: "all",   label: "All time" },
                      { val: "today", label: "Today" },
                      { val: "week",  label: "Last 7 days" },
                      { val: "month", label: "Last 30 days" },
                    ].map(({ val, label }) => (
                      <button
                        key={val}
                        data-testid={`${DASHBOARD.dateFilterButton}-${val}`}
                        onClick={() => setDateFilter(val)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                          dateFilter === val
                            ? "bg-blue-900 text-white border-blue-900"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px bg-slate-200 self-stretch mx-1 hidden sm:block" />

                {/* Sort */}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sort</span>
                  <div className="relative">
                    <select
                      data-testid={DASHBOARD.sortSelect}
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none pl-3 pr-7 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer hover:border-blue-300 transition-all">
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="name">Patient name</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            )}

            {/* Results summary + clear */}
            {(hasActiveFilters || filteredReports.length !== reports.length) && (
              <div className="flex items-center justify-between text-sm">
                <span data-testid={DASHBOARD.resultsCount} className="text-slate-500">
                  {filteredReports.length === reports.length
                    ? `${reports.length} report${reports.length !== 1 ? "s" : ""}`
                    : `${filteredReports.length} of ${reports.length} report${reports.length !== 1 ? "s" : ""}`}
                </span>
                {hasActiveFilters && (
                  <button
                    data-testid={DASHBOARD.clearFiltersButton}
                    onClick={clearFilters}
                    className="text-blue-900 font-medium hover:underline flex items-center gap-1 text-xs">
                    <X size={12} /> Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reports */}
        {loading ? (
          <div data-testid={DASHBOARD.loadingState} className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div data-testid={DASHBOARD.emptyState} className="text-center py-20">
            <img
              src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=400&q=60"
              alt="No reports"
              className="w-48 h-32 object-cover rounded-xl mx-auto mb-6 opacity-70"
            />
            <h3 className="text-xl font-medium text-slate-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>No reports yet</h3>
            <p className="text-slate-400 text-sm mb-6">Upload a blood test PDF to get started</p>
            <button
              data-testid={DASHBOARD.uploadFirstButton}
              onClick={() => navigate("/upload")}
              className="bg-blue-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors">
              Upload Your First Report
            </button>
          </div>
        ) : filteredReports.length === 0 ? (
          <div data-testid={DASHBOARD.noResultsState} className="text-center py-16 bg-white border border-slate-200 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Search size={20} className="text-slate-400" />
            </div>
            <h3 className="text-base font-medium text-slate-700 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>No matching reports</h3>
            <p className="text-slate-400 text-sm mb-4">Try adjusting your search or filters</p>
            <button
              data-testid={DASHBOARD.clearFiltersButton}
              onClick={clearFilters}
              className="text-blue-900 text-sm font-medium hover:underline">
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={DASHBOARD.reportsList}>
            {filteredReports.map((report, i) => {
              const abnCount = getAbnormalCount(report);
              return (
                <div
                  key={report._id}
                  data-testid={`${DASHBOARD.reportCard}-${i}`}
                  onClick={() => navigate(`/reports/${report._id}`)}
                  className="bg-white border border-slate-200 rounded-lg p-5 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all duration-200 group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText size={16} className="text-blue-900" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 leading-tight">
                          {report.patient_info?.name || "Unknown Patient"}
                        </p>
                        <p className="text-xs text-slate-400">{report.original_filename}</p>
                      </div>
                    </div>
                    <StatusBadge status={report.overall_status} />
                  </div>

                  <p className="text-xs text-slate-500 line-clamp-2 mb-4 leading-relaxed">
                    {report.overview || "No overview available"}
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock size={11} /> {formatDate(report.created_at)}
                      </span>
                      {abnCount > 0 && (
                        <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          {abnCount} abnormal
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`delete-report-${report._id}`}
                        onClick={(e) => openDeleteModal(e, report)}
                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                        <Trash2 size={13} />
                      </button>
                      <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-900 transition-colors" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
